/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { AccountBookOutlined, RadarChartOutlined } from '@ant-design/icons'
import { Breadcrumb, Button, Descriptions, DescriptionsProps, Space } from 'antd'
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import styles from './index.less'
import { StateTag } from '@/components/StateTag'
import { useTranslateAmount } from '@/hooks/useTranslateAmount'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { SEARCH_PURCHASES_TIME } from '@/pages/billing/CostAnalysis'
import { ProductPurchases } from '@/pages/billing/StatementDetail/ProductPurchases'
import { ProductSummary } from '@/pages/billing/StatementDetail/ProductSummary'
import { ConfirmStatement } from '@/pages/billing/features/ConfirmStatement'
import { DownloadStatement } from '@/pages/billing/features/DownloadStatement'
import { StatementConfirmState } from '@/pages/billing/interface'
import { RouteSupplyData } from '@/routes/RouteSupplyData'
import { timeFormatter } from '@/utils/timeFormatter'

export function StatementDetail() {
  const { t } = useTranslation()
  const { contractId, statementId } = useParams()
  const { currency } = useTranslateAmount()
  const { data: statementDetail } = RouteSupplyData.useContext('statementId')
  const { dueDate, cashDeductedFormatted, undeductedAmountFormatted, confirmState, deductState } = statementDetail
  const { statementStart, amountFormatted, voucherDeductedAmountFormatted, payableAmountFormatted, contractName } = statementDetail.statementData

  const items: ItemType[] = [
    {
      title: (
        <>
          <AccountBookOutlined />
          <Link to={`/billing-center/${contractId}/statements`}>{t('Statements')}</Link>
        </>
      ),
    },
    {
      title: `${timeFormatter.format(statementStart, 'yearMonth')} ${t('Statement Detail')}`,
    },
  ]

  const baseInfoItems: DescriptionsProps['items'] = [
    {
      key: 'buyerName',
      label: t('Customer Name'),
      children: contractName,
    },
    {
      key: 'contractID',
      label: t('Contract ID'),
      children: contractId,
    },
    {
      key: 'statementStart',
      label: t('Statement Cycle'),
      children: `${timeFormatter.format(dayjs(statementStart).startOf('month'), 'date')} ${t('to')} ${timeFormatter.format(dayjs(statementStart).endOf('month'), 'date')}`,
    },
    {
      key: 'statementType',
      label: t('Statement Type'),
      children: t('Usage-based billing (monthly billing generated based on actual resource consumption time)'),
    },
    {
      key: 'start',
      label: t('Statement Date'),
      children: timeFormatter.format(dayjs(statementStart).endOf('month').add(2, 'days'), 'date'),
    },
    {
      key: 'dueDate',
      label: t('Due Date'),
      children: timeFormatter.format(dueDate, 'date'),
    },
  ]

  return (
    <LayoutOthers.MainContent
      title={<Breadcrumb items={items} />}
      extra={
        <Space>
          <ConfirmStatement isConfirmed={statementDetail.confirmState === StatementConfirmState.confirmed} statementId={statementId} />
          <DownloadStatement text={t('Download Statement')} statementId={statementId} />
          <Link to={`/billing-center/${contractId}/cost-analysis?${SEARCH_PURCHASES_TIME}=${window.encodeURIComponent(statementStart)}`}>
            <Button icon={<RadarChartOutlined />}>{t('Cost Analysis')}</Button>
          </Link>
        </Space>
      }
    >
      <LayoutOthers.ContentCard title={t('Base Info')}>
        <Descriptions items={baseInfoItems} column={2} size="small" bordered />
      </LayoutOthers.ContentCard>
      <LayoutOthers.ContentCard
        title={t('Statement Amount')}
        extra={<StateTag state={confirmState === StatementConfirmState.confirmed ? deductState : confirmState} />}
        className={styles.spaceAround}
      >
        <section>
          <div>{t('Consumption Amount')}</div>
          <div className={styles.amount}>
            {amountFormatted}
            <span className="small-currency"> {currency}</span>
          </div>
        </section>
        <section>
          <div>{t('Vouchers Redeemed')}</div>
          <div className={styles.amount}>
            {voucherDeductedAmountFormatted}
            <span className="small-currency"> {currency}</span>
          </div>
        </section>
        <section>
          <div>{t('Payable Amount')}</div>
          <Space size="large">
            <span className={styles.amount}>
              {payableAmountFormatted}
              <span className="small-currency"> {currency}</span>
            </span>
            <span>
              <div className="light-label">{`${t('Undeducted')}: ${undeductedAmountFormatted} ${currency}`}</div>
              <div className="light-label">{`${t('Deducted')}: ${cashDeductedFormatted} ${currency}`}</div>
            </span>
          </Space>
        </section>
      </LayoutOthers.ContentCard>
      <LayoutOthers.ContentCard title={t('Summary by Product')}>
        <ProductSummary />
      </LayoutOthers.ContentCard>
      <LayoutOthers.ContentCard title={t('Statement Details')}>
        <ProductPurchases />
      </LayoutOthers.ContentCard>
    </LayoutOthers.MainContent>
  )
}
